import { isAuthRetryableFetchError } from "@supabase/supabase-js";
import { AxiosError } from "axios";

interface ApiErrorResponse {
  message: string;
  detail: string | null;
}

const FALLBACK_MESSAGE = "An unexpected error occurred. Please try again later.";

const RATE_LIMIT_ERROR_MESSAGE = "Too many submissions. Please wait a few minutes and try again.";

const NETWORK_FAILURE_ERROR_MESSAGE = "Network error. Check your connection and try again.";

const AUTH_UNREACHABLE_ERROR_MESSAGE =
  "Can't reach the sign-in service. Try a different network or browser.";

/**
 * Extracts error messages from API responses.
 * Backend always returns { message, detail } structure.
 * @param err - The error object (typically from axios)
 * @returns The error message (always present, falls back to FALLBACK_MESSAGE)
 */
export const getErrorMessage = (err: AxiosError<{ message?: string }>): string => {
  return err?.response?.data?.message ?? FALLBACK_MESSAGE;
};

/**
 * Extracts error messages from Supabase Auth API responses.
 * Supabase AuthError has a message property (e.g. "Invalid login credentials").
 * - Only status 0 (browser fetch failure) is remapped; 502/503/504 keep their server message.
 * @param err - The error object from Supabase auth (signIn, signUp, etc.)
 * @returns The error message (always present, falls back to FALLBACK_MESSAGE)
 */
export const getSupabaseErrorMessage = (err: Error): string => {
  if (isAuthRetryableFetchError(err) && err.status === 0) {
    return AUTH_UNREACHABLE_ERROR_MESSAGE;
  }
  const message = err?.message;
  return typeof message === "string" && message.trim() ? message : FALLBACK_MESSAGE;
};

/**
 * Checks if an error is a network error (no response from server).
 * @param err - The error object (typically from axios)
 * @returns True if the error is a network error (no response), false otherwise
 */
export const isNetworkError = (err: AxiosError<ApiErrorResponse>): boolean => {
  return !err?.response;
};

/**
 * True when the error is an Axios response with the given HTTP status (e.g. 404, 409).
 */
export const isAxiosHttpStatus = (
  err: AxiosError<ApiErrorResponse>,
  statusCode: number
): boolean => {
  return err?.response?.status === statusCode;
};

/**
 * Checks if an error indicates a conflict (HTTP 409) or concurrent modification.
 * @param err - The error object (typically from axios)
 * @returns True if the error is a conflict/concurrent update error, false otherwise
 */
export const isConflictError = (err: AxiosError<ApiErrorResponse>): boolean => {
  return isAxiosHttpStatus(err, 409);
};

interface FastAPIValidationDetailItem {
  msg?: string;
}

type FastAPIErrorBody = {
  detail?: string | FastAPIValidationDetailItem[];
  message?: string;
  errors?: Array<{ field: string; message: string }>;
};

/**
 * Field-level items from the API validation handler (`errors` on HTTP 422), if present.
 */
export const getRequestValidationErrorItems = (
  err: AxiosError<FastAPIErrorBody>
): Array<{ field: string; message: string }> => {
  const data = err.response?.data;
  if (!data?.errors || !Array.isArray(data.errors)) {
    return [];
  }
  return data.errors.filter(
    (item): item is { field: string; message: string } =>
      typeof item === "object" &&
      item !== null &&
      typeof item.field === "string" &&
      typeof item.message === "string"
  );
};

/**
 * User-visible copy for HTTP 429 (shared wording for the whole app).
 * @returns The rate-limit message if the error is an Axios 429 response, otherwise `null`.
 */
export const getAxiosRateLimitErrorMessage = (err: AxiosError<ApiErrorResponse>): string | null => {
  return isAxiosHttpStatus(err, 429) ? RATE_LIMIT_ERROR_MESSAGE : null;
};

/**
 * Fixed copy when Axios has no response (offline, DNS, timeout, etc.).
 * @returns The network-failure message if applicable, otherwise `null`.
 */
export const getAxiosNetworkFailureErrorMessage = (
  err: AxiosError<ApiErrorResponse>
): string | null => {
  return isNetworkError(err) ? NETWORK_FAILURE_ERROR_MESSAGE : null;
};

/**
 * Reads validation messages from an Axios error body: our `{ errors: { field, message }[] }`
 * shape (HTTP 422), then FastAPI's `detail` (`{ msg }[]` or string), then `message`.
 */
export const getFastApiValidationDetailMessage = (err: AxiosError<FastAPIErrorBody>): string => {
  const requestValidationItems = getRequestValidationErrorItems(err);
  if (requestValidationItems.length > 0) {
    return requestValidationItems.map((i) => i.message).join(" ");
  }
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) => (typeof d === "object" && d?.msg ? d.msg : null))
      .filter(Boolean);
    if (messages.length > 0) {
      return messages.join(" ");
    }
  }
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  return getErrorMessage(err);
};
